import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { message } from "antd";
import type { ApiResponse } from "types/api";

// 创建 axios 实例
const instance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "/api",
  timeout: 60000,
  headers: {
    "Content-Type": "application/json",
  },
});

// 重试配置
const RETRY_CONFIG = {
  retries: 3,
  retryDelay: 1000,
  retryCondition: (error: AxiosError) => {
    // 只在网络错误或5xx错误时重试
    return (
      !error.response ||
      (error.response.status >= 500 && error.response.status < 600)
    );
  },
};

// 重试函数
const retryRequest = async (
  config: AxiosRequestConfig,
  retryCount = 0,
): Promise<any> => {
  try {
    return await instance.request(config);
  } catch (error) {
    const axiosError = error as AxiosError;

    if (
      retryCount < RETRY_CONFIG.retries &&
      RETRY_CONFIG.retryCondition(axiosError)
    ) {
      console.log(
        `请求失败，正在重试 (${retryCount + 1}/${RETRY_CONFIG.retries}):`,
        config.url,
      );
      await new Promise((resolve) =>
        setTimeout(resolve, RETRY_CONFIG.retryDelay * (retryCount + 1)),
      );
      return retryRequest(config, retryCount + 1);
    }

    throw error;
  }
};

// 请求拦截器
instance.interceptors.request.use(
  (config) => {
    // 从 localStorage 获取 token（如果有的话）
    const token = localStorage.getItem("token");
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    console.error("请求错误:", error);
    return Promise.reject(error);
  },
);

// 响应拦截器
instance.interceptors.response.use(
  (response) => {
    const { data } = response;

    // 根据业务状态码处理
    // code 为 0, 1, 200 都视为成功
    if (data.code !== undefined && ![0, 1, 200].includes(data.code)) {
      // 只有失败时才显示错误消息
      message.error(data.msg || "请求失败");
      return Promise.reject(new Error(data.msg || "请求失败"));
    }

    return response;
  },
  (error: AxiosError) => {
    // 处理 HTTP 错误
    if (error.response) {
      const status = error.response.status;
      switch (status) {
        case 401:
          message.error("未授权，请登录");
          // 可以在这里处理登出逻辑
          localStorage.removeItem("token");
          break;
        case 403:
          message.error("拒绝访问");
          break;
        case 404:
          message.error("请求的资源不存在");
          break;
        case 500:
          message.error("服务器错误");
          break;
        default:
          message.error(error.message || "请求失败");
      }
    } else if (error.request) {
      // 网络错误处理
      console.error("网络错误详情:", error);
      if (error.code === "ECONNREFUSED") {
        message.error("无法连接到服务器，请检查后端服务是否启动");
      } else if (error.code === "NETWORK_ERROR") {
        message.error("网络连接失败，请检查网络设置");
      } else {
        message.error("网络错误，请检查网络连接");
      }
    } else {
      message.error("请求配置错误");
    }

    return Promise.reject(error);
  },
);

/**
 * GET 请求
 * @param url 请求地址
 * @param params 请求参数
 * @param config 额外配置
 */
export function get<T = any>(
  url: string,
  params?: Record<string, any>,
  config?: AxiosRequestConfig,
): Promise<ApiResponse<T>> {
  return retryRequest({
    method: "GET",
    url,
    params,
    ...config,
  }).then((response) => response.data);
}

/**
 * POST 请求
 * @param url 请求地址
 * @param data 请求数据
 * @param config 额外配置
 */
export function post<T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
): Promise<ApiResponse<T>> {
  return retryRequest({
    method: "POST",
    url,
    data,
    ...config,
  }).then((response) => response.data);
}

/**
 * PUT 请求
 * @param url 请求地址
 * @param data 请求数据
 * @param config 额外配置
 */
export function put<T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
): Promise<ApiResponse<T>> {
  return retryRequest({
    method: "PUT",
    url,
    data,
    ...config,
  }).then((response) => response.data);
}

/**
 * DELETE 请求
 * @param url 请求地址
 * @param config 额外配置
 */
export function del<T = any>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<ApiResponse<T>> {
  return retryRequest({
    method: "DELETE",
    url,
    ...config,
  }).then((response) => response.data);
}

export default instance;
